/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Canvas,
  Object as FabricObject,
  util as fabricUtil,
  Image,
  StaticCanvas,
  Textbox,
} from 'fabric';

import { useCanvasStore } from '../../store';

import { TextEntity } from '../../metaBullets/classes/TextEntity';

import { Layer } from '../../types/types';

import { CANVAS_EVENTS } from '../../config/constants';

import { Marker } from '../../metaBullets';

import { scaleImage } from '../imageUtils';
import { applyLockToObject } from '../lockObjects';
import { reorderCanvasObjects } from '../layersUtils';
import { extractSlideMetadata } from '../slideMetadata';
import { setCanvasBackgroundImage } from '../backgroundImage';
import { processLoadingGroupFromConfig } from '../groupUtils';
import { initializeMetaBulletsFromCanvas } from '../metaBullets/initializeFromCanvas';
import { makeColorizedUrlForObject } from '../makeColorizedUrlForObject';
import {
  createPlaceholderForImage,
  replacePlaceholderWithImage,
  removePlaceholderOnError,
} from '../progressivePlaceholders';
import { FABRIC_CUSTOM_PROPERTIES } from '../UniqueFabricObject';

export type LoadOptions = {
  progressive?: boolean;
  lockDuringLoad?: boolean;
  streamingRender?: boolean;
  onProgress?: (p: { loaded: number; total: number }) => void;
  logging?: boolean;
  clearCanvasBeforeLoad?: boolean; // Флаг для очистки канваса и layersMap перед загрузкой
};

type LayersMap = { [K in Layer]: FabricObject[] };

type LoadCtx = {
  canvas: Canvas | StaticCanvas;
  canvasId: string;
  slideWidth: number;
  slideHeight: number;
  progressive: boolean;
  lockDuringLoad: boolean;
  streamingRender: boolean;
  logging: boolean;
  onProgress?: (p: { loaded: number; total: number }) => void;
  total: number;
  loaded: number;
  report: () => void;
  layersMap: LayersMap;
  textEntityStyles?: unknown;
  isNeedMutateStore: boolean;
  canvasBackgroundImage?: string | null;
  isBackgroundSetted: boolean;
  replacementPromises: Promise<void>[];
};

const LAYER_ORDER: Layer[] = ['background', 'middle', 'foreground', 'overlay'];

function createLayersMap(): LayersMap {
  return { background: [], middle: [], foreground: [], overlay: [] };
}

function buildCtx(args: {
  canvas: Canvas | StaticCanvas;
  canvasId: string;
  slideWidth: number;
  slideHeight: number;
  progressive: boolean;
  lockDuringLoad: boolean;
  streamingRender: boolean;
  logging: boolean;
  onProgress?: (p: { loaded: number; total: number }) => void;
  total: number;
  textEntityStyles?: unknown;
  isNeedMutateStore: boolean;
  canvasBackgroundImage?: string | null;
}): LoadCtx {
  const ctx: LoadCtx = {
    ...args,
    loaded: 0,
    report: () => args.onProgress?.({ loaded: ctx.loaded, total: ctx.total }),
    layersMap: createLayersMap(),
    isBackgroundSetted: false,
    replacementPromises: [],
  };
  return ctx;
}

function addObjectToCanvasWithOrder(ctx: LoadCtx, obj: FabricObject, layerName: Layer) {
  if (!ctx.streamingRender) return;
  const canvas = ctx.canvas as Canvas;
  const objZIndex = obj.zIndex || 0;
  const objId = (obj as any).id;

  // Проверяем, нет ли уже объекта с таким id на канвасе
  const existingObjOnCanvas = objId ? canvas.getObjects().find((o: any) => o.id === objId) : null;

  if (existingObjOnCanvas) {
    console.warn('⚠️ [addObjectToCanvasWithOrder] Объект уже на канвасе, пропускаем:', {
      objId,
      objType: obj.type,
      layerName,
      existingObjType: existingObjOnCanvas.type,
    });
    return;
  }

  const canvasObjects = canvas.getObjects();
  let insertIndex = canvasObjects.length;
  const targetLayerIndex = LAYER_ORDER.indexOf(layerName);

  for (let i = 0; i < canvasObjects.length; i++) {
    const existingObj = canvasObjects[i] as any;
    const existingLayer = existingObj.layer || 'background';
    const existingZIndex = existingObj.zIndex || 0;
    const existingLayerIndex = LAYER_ORDER.indexOf(existingLayer);

    if (existingLayerIndex > targetLayerIndex) {
      insertIndex = i;
      break;
    }
    if (existingLayerIndex === targetLayerIndex && existingZIndex > objZIndex) {
      insertIndex = i;
      break;
    }
  }

  // console.log('➕ [addObjectToCanvasWithOrder] Добавляем объект на канвас:', {
  //   objId,
  //   objType: obj.type,
  //   layerName,
  //   insertIndex,
  //   canvasObjectsBefore: canvasObjects.length,
  // });

  canvas.insertAt(insertIndex, obj);
  canvas.requestRenderAll();
}

async function handleGroup(ctx: LoadCtx, objData: any, layerName: Layer) {
  const preparedGroup = await processLoadingGroupFromConfig(objData);
  if (!preparedGroup) {
    ctx.loaded++;
    ctx.report();
    return;
  }
  preparedGroup.layer = layerName;
  ctx.layersMap[layerName].push(preparedGroup);
  addObjectToCanvasWithOrder(ctx, preparedGroup, layerName);
  ctx.loaded++;
  ctx.report();
}

function handleMarker(ctx: LoadCtx, objData: any, layerName: Layer) {
  try {
    const marker = new Marker({
      ...objData,
      entityId: objData.entityId,
      entityType: objData.entityType,
      type: objData.markerType,
      content: objData.content,
      id: objData.id,
      left: objData.left || 0,
      top: objData.top || 0,
      width: objData.width || 16,
      height: objData.height || 16,
    });
    marker.layer = layerName;
    // console.log('  📝 [handleMarker] Добавляем в ctx.layersMap:', {
    //   objId: marker.id,
    //   layerName,
    //   layerCountBefore: ctx.layersMap[layerName].length,
    // });
    ctx.layersMap[layerName].push(marker);
    addObjectToCanvasWithOrder(ctx, marker, layerName);
  } finally {
    ctx.loaded++;
    ctx.report();
  }
}

async function handleTextEntity(ctx: LoadCtx, objData: any, layerName: Layer) {
  try {
    const textEntity = new TextEntity(objData.text || '', {
      ...objData,
      isMetaBulletEntity: true,
      // ✅ Передаем stylesConfig ДО создания, чтобы он был доступен в конструкторе
      stylesConfig: ctx.textEntityStyles || objData.stylesConfig,
    });
    // Обновление через updateStylesFromCanvasConfig больше не нужно, т.к. stylesConfig уже установлен
    // if (ctx.textEntityStyles) {
    //   textEntity.updateStylesFromCanvasConfig({ textEntityStyles: ctx.textEntityStyles });
    // } else if (objData.stylesConfig) {
    //   textEntity.updateStylesConfig(objData.stylesConfig);
    // }
    Object.keys(objData).forEach((key) => {
      if (key !== 'text' && key !== 'type' && !(key in textEntity)) {
        (textEntity as any)[key] = objData[key];
      }
    });
    if (objData.isLocked) applyLockToObject(textEntity, true);
    textEntity.layer = layerName;
    // console.log('  📝 [handleTextEntity] Добавляем в ctx.layersMap:', {
    //   objId: textEntity.id,
    //   layerName,
    //   layerCountBefore: ctx.layersMap[layerName].length,
    // });
    ctx.layersMap[layerName].push(textEntity);
    addObjectToCanvasWithOrder(ctx, textEntity, layerName);
  } catch {
    const fallbackObjects = await fabricUtil.enlivenObjects([objData]);
    fallbackObjects.forEach((obj) => {
      if (obj instanceof FabricObject) {
        (obj as any).layer = layerName;
        ctx.layersMap[layerName].push(obj);
        addObjectToCanvasWithOrder(ctx, obj, layerName);
      }
    });
  } finally {
    ctx.loaded++;
    ctx.report();
  }
}

async function handleProgressiveImage(ctx: LoadCtx, objData: any, layerName: Layer) {
  const canvas = ctx.canvas as Canvas;
  const placeholder = createPlaceholderForImage(objData);
  (placeholder as any).layer = layerName;

  if (ctx.streamingRender) {
    addObjectToCanvasWithOrder(ctx, placeholder as FabricObject, layerName);
    const label = (placeholder as any).__placeholderLabel;
    if (label) addObjectToCanvasWithOrder(ctx, label, layerName);
  } else {
    canvas.add(placeholder as FabricObject);
    const label = (placeholder as any).__placeholderLabel;
    if (label) canvas.add(label);
    canvas.requestRenderAll();
  }

  ctx.layersMap[layerName].push(placeholder as FabricObject);

  const p = (async () => {
    try {
      const objectsFromObjData = await fabricUtil.enlivenObjects([objData]);
      const img = objectsFromObjData.find(
        (o) => o instanceof FabricObject && o.type?.toLowerCase() === 'image',
      );
      if (img && img instanceof Image) {
        img.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
        // 🎯 АВТОМАТИЧЕСКОЕ масштабирование для картинок-контента маркеров
        // Если у картинки есть markerId - масштабируем, если флаг не установлен явно в false
        const hasMarkerId = (img as any).markerId;
        const scaleFlag = (img as any).isNeedScaleImgForFirstRender;
        const isScaleFlagExplicitlyFalse = scaleFlag === false;

        // Масштабируем если:
        // 1. Флаг true - всегда масштабируем
        // 2. Флаг undefined/отсутствует И есть markerId - принудительно масштабируем
        // 3. Флаг undefined/отсутствует И НЕТ markerId - масштабируем по умолчанию
        // НЕ масштабируем только если флаг явно false
        const shouldScale =
          !isScaleFlagExplicitlyFalse &&
          (scaleFlag === true || (scaleFlag === undefined && (hasMarkerId || true)));

        if (shouldScale) {
          // console.log('🎯 [handleProgressiveImage] scaleImage:', {
          //   imgId: img.id,
          //   markerId: hasMarkerId,
          //   scaleFlag,
          //   isScaleFlagExplicitlyFalse,
          //   reason:
          //     scaleFlag === true
          //       ? 'flag true'
          //       : hasMarkerId
          //         ? 'markerId + undefined flag'
          //         : 'default scaling',
          // });
          scaleImage(img as Image, (img as any).width, (img as any).height);
        }
        // ✅ Включаем попиксельное определение клика для изображений с маской
        if (img.clipPath) {
          img.perPixelTargetFind = true;
        }
        await replacePlaceholderWithImage(
          canvas,
          ctx.canvasId,
          layerName,
          placeholder as FabricObject,
          img as Image,
          ctx.isNeedMutateStore,
        );
      } else {
        await removePlaceholderOnError(
          canvas,
          ctx.canvasId,
          layerName,
          placeholder as FabricObject,
          ctx.isNeedMutateStore,
        );
      }
    } catch {
      await removePlaceholderOnError(
        canvas,
        ctx.canvasId,
        layerName,
        placeholder as FabricObject,
        ctx.isNeedMutateStore,
      );
    } finally {
      ctx.loaded++;
      ctx.report();
    }
  })();
  ctx.replacementPromises.push(p);
}

async function handleGenericObject(ctx: LoadCtx, objData: any, layerName: Layer) {
  const { updateMetaData } = useCanvasStore.getState();
  const canvas = ctx.canvas as Canvas;

  const objectsFromObjData = await fabricUtil.enlivenObjects([objData]);

  if (objData.category) {
    if (objData.category?.toLowerCase() === 'metadata' && ctx.isNeedMutateStore) {
      updateMetaData(ctx.canvasId, objData);
    }
    if (objData.category?.toLowerCase() === 'background') {
      if (objData.backgroundColor) {
        canvas.backgroundColor = objData.backgroundColor;
        ctx.isBackgroundSetted = true;
      }
      if (objData.backgroundImage) {
        try {
          ctx.canvasBackgroundImage = objData.backgroundImage;
          if (!ctx.progressive && ctx.canvasBackgroundImage) {
            await setCanvasBackgroundImage(
              canvas,
              ctx.canvasBackgroundImage,
              ctx.slideWidth,
              ctx.slideHeight,
            );
            updateMetaData(ctx.canvasId, { isHaveBackgroundImage: true });
            ctx.isBackgroundSetted = true;
          }
        } catch {
          updateMetaData(ctx.canvasId, { isHaveBackgroundImage: false });
        }
      }
    }
  }

  objectsFromObjData.forEach((obj) => {
    if (!(obj instanceof FabricObject)) return;

    (obj as any).layer = layerName;

    // КРИТИЧНО: enlivenObjects не восстанавливает кастомные свойства автоматически!
    // Копируем все кастомные свойства из objData в восстановленный объект
    FABRIC_CUSTOM_PROPERTIES.forEach((prop) => {
      if (objData[prop] !== undefined) {
        (obj as any)[prop] = objData[prop];
      }
    });

    if ('isLocked' in (obj as any) && (obj as any).isLocked) {
      applyLockToObject(obj, true);
    }
    if ('type' in obj && obj.type?.toLowerCase() === 'image' && obj instanceof Image) {
      obj.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
      const imageObject = obj as Image;
      // 🎯 АВТОМАТИЧЕСКОЕ масштабирование для картинок-контента маркеров
      // Если у картинки есть markerId - масштабируем, если флаг не установлен явно в false
      const hasMarkerId = (imageObject as any).markerId;
      const scaleFlag = (imageObject as any).isNeedScaleImgForFirstRender;
      const isScaleFlagExplicitlyFalse = scaleFlag === false;

      // Масштабируем если:
      // 1. Флаг true - всегда масштабируем
      // 2. Флаг undefined/отсутствует И есть markerId - принудительно масштабируем
      // 3. Флаг undefined/отсутствует И НЕТ markerId - масштабируем по умолчанию
      // НЕ масштабируем только если флаг явно false
      const shouldScale =
        !isScaleFlagExplicitlyFalse &&
        (scaleFlag === true || (scaleFlag === undefined && (hasMarkerId || true)));

      if (shouldScale) {
        // console.log('🎯 [handleGenericObject] scaleImage:', {
        //   imgId: imageObject.id,
        //   markerId: hasMarkerId,
        //   scaleFlag,
        //   isScaleFlagExplicitlyFalse,
        //   reason:
        //     scaleFlag === true
        //       ? 'flag true'
        //       : hasMarkerId
        //         ? 'markerId + undefined flag'
        //         : 'default scaling',
        // });
        scaleImage(imageObject, (imageObject as any).width, (imageObject as any).height);
      }
      // ✅ Включаем попиксельное определение клика для изображений с маской
      if (imageObject.clipPath) {
        imageObject.perPixelTargetFind = true;
      }
      // console.log('  📝 [handleGenericObject] Добавляем изображение в ctx.layersMap:', {
      //   objId: imageObject.id,
      //   layerName,
      //   layerCountBefore: ctx.layersMap[layerName].length,
      // });
      ctx.layersMap[layerName].push(imageObject as unknown as FabricObject);
      addObjectToCanvasWithOrder(ctx, imageObject as unknown as FabricObject, layerName);
    } else if (obj instanceof Textbox) {
      if ((obj as any).originalText === undefined || (obj as any).originalText === null) {
        obj.set('originalText', (obj as any).text);
      }
      // console.log('  📝 [handleGenericObject] Добавляем Textbox в ctx.layersMap:', {
      //   objId: (obj as any).id,
      //   layerName,
      //   layerCountBefore: ctx.layersMap[layerName].length,
      // });
      ctx.layersMap[layerName].push(obj);
      addObjectToCanvasWithOrder(ctx, obj, layerName);
    } else {
      // console.log('  📝 [handleGenericObject] Добавляем объект в ctx.layersMap:', {
      //   objId: (obj as any).id,
      //   objType: obj.type,
      //   layerName,
      //   layerCountBefore: ctx.layersMap[layerName].length,
      // });
      ctx.layersMap[layerName].push(obj);
      addObjectToCanvasWithOrder(ctx, obj, layerName);
    }
  });

  ctx.loaded++;
  ctx.report();
}

async function commitLayersToCanvasAndStore(ctx: LoadCtx) {
  const { addLayerObject, getCanvasState } = useCanvasStore.getState();
  const canvas = ctx.canvas as Canvas;

  const canvasState = getCanvasState(ctx.canvasId);
  const storeLayersMap = (canvasState?.layersMap || {}) as { [key in Layer]?: any[] };

  // console.log('📦 [commitLayersToCanvasAndStore] Начало коммита:', {
  //   canvasId: ctx.canvasId,
  //   streamingRender: ctx.streamingRender,
  //   progressive: ctx.progressive,
  //   isNeedMutateStore: ctx.isNeedMutateStore,
  //   canvasObjectsBefore: canvas.getObjects().length,
  //   storeObjectsBefore: Object.values(storeLayersMap).flat().length,
  // });

  for (const layer in ctx.layersMap) {
    const objectsInLayer = ctx.layersMap[layer as Layer];
    const sortedObjects = objectsInLayer.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

    // console.log(`  📋 Слой ${layer}: ${sortedObjects.length} объектов`);

    sortedObjects.forEach((obj) => {
      const objId = (obj as any).id;
      const isPlaceholder = (obj as any).fill === '#EFF1F3' && (obj as any).stroke === '#D2D6DB';
      const isPlaceholderLabel = (obj as any).isPlaceholderLabel;

      // Проверяем, есть ли уже объект в store
      const layerObjects = storeLayersMap[layer as Layer] || [];
      const existingInStore = layerObjects.find((item: any) => item.object.id === objId);

      if (existingInStore) {
        console.warn(`  ⚠️ Объект уже в store (слой ${layer}), пропускаем добавление:`, {
          objId,
          objType: obj.type,
        });
        // Пропускаем добавление в store, если объект уже там есть
        // Продолжаем обработку для добавления на канвас (если нужно)
      }

      if ((ctx.progressive || ctx.streamingRender) && (isPlaceholder || isPlaceholderLabel)) {
        // already on canvas
        // console.log(`  ⏭️ Пропуск placeholder (слой ${layer}):`, { objId, objType: obj.type });
      } else if (ctx.streamingRender) {
        // already on canvas
        // console.log(`  ⏭️ Пропуск добавления на канвас (streamingRender, слой ${layer}):`, {
        //   objId,
        //   objType: obj.type,
        // });
      } else {
        // console.log(`  ➕ Добавляем на канвас (слой ${layer}):`, { objId, objType: obj.type });
        canvas.add(obj);
      }
      if (ctx.isNeedMutateStore && !existingInStore) {
        // console.log(`  📝 Добавляем в store (слой ${layer}):`, { objId, objType: obj.type });
        addLayerObject(ctx.canvasId, layer as Layer, {
          object: obj,
          zIndex: obj.zIndex || 0,
          layer: layer as Layer,
        });
      }
      if (!ctx.progressive && !ctx.streamingRender) {
        const label = (obj as any).__placeholderLabel;
        if (label) canvas.add(label);
      }
    });
  }

  // console.log('✅ [commitLayersToCanvasAndStore] Коммит завершен:', {
  //   canvasId: ctx.canvasId,
  //   canvasObjectsAfter: canvas.getObjects().length,
  //   storeObjectsAfter: Object.values(getCanvasState(ctx.canvasId)?.layersMap || {}).flat().length,
  // });
}

async function finalizeBackground(ctx: LoadCtx) {
  const canvas = ctx.canvas as Canvas;
  // Устанавливаем фоновое изображение если:
  // 1. Оно задано (из объекта category:background или из верхнего уровня backup)
  // 2. Фон ещё не был установлен (ctx.isBackgroundSetted === false)
  console.debug('🎨 [finalizeBackground] Called:', {
    canvasId: ctx.canvasId,
    hasBackgroundImage: !!ctx.canvasBackgroundImage,
    backgroundImageUrl: ctx.canvasBackgroundImage?.substring(0, 80),
    isBackgroundSetted: ctx.isBackgroundSetted,
  });

  if (ctx.canvasBackgroundImage && !ctx.isBackgroundSetted) {
    try {
      console.debug('🎨 [finalizeBackground] Setting background image...');
      await setCanvasBackgroundImage(
        canvas,
        ctx.canvasBackgroundImage,
        ctx.slideWidth,
        ctx.slideHeight,
      );
      useCanvasStore.getState().updateMetaData(ctx.canvasId, { isHaveBackgroundImage: true });
      ctx.isBackgroundSetted = true;
      console.debug('🎨 [finalizeBackground] Background image set successfully');
    } catch (error) {
      console.warn('⚠️ [finalizeBackground] Failed to set background image:', error);
    }
  }
}

async function postProgressiveFinalize(ctx: LoadCtx, objects: any[]) {
  const canvas = ctx.canvas as Canvas;

  console.debug('⏳ [postProgressiveFinalize] Waiting for replacement promises:', {
    canvasId: ctx.canvasId,
    promisesCount: ctx.replacementPromises.length,
  });

  await Promise.all(ctx.replacementPromises);

  console.debug('✅ [postProgressiveFinalize] All replacement promises resolved:', {
    canvasId: ctx.canvasId,
  });

  if (canvas instanceof Canvas) {
    const { getCanvasState } = useCanvasStore.getState();
    const currentCanvasData = getCanvasState(ctx.canvasId);
    if (currentCanvasData?.layersMap) {
      reorderCanvasObjects(canvas, currentCanvasData.layersMap, ctx.canvasId);
    }
  }
  if (ctx.canvasBackgroundImage && canvas instanceof Canvas) {
    await setCanvasBackgroundImage(
      canvas,
      ctx.canvasBackgroundImage,
      ctx.slideWidth,
      ctx.slideHeight,
    );
  }
  canvas.requestRenderAll();
  await new Promise((resolve) => setTimeout(resolve, 50));

  if (canvas instanceof Canvas) {
    const event = new CustomEvent(CANVAS_EVENTS.PROGRESSIVE_LOAD_COMPLETE, {
      detail: {
        canvasId: ctx.canvasId,
        totalImages: objects.filter((obj) => obj.type?.toLowerCase() === 'image').length,
      },
    });
    document.dispatchEvent(event);
  }
}

async function handleObject(ctx: LoadCtx, objData: any) {
  const layerName = (objData.layer as Layer) || 'background';
  // const objId = objData.id;
  // const objType = objData.type;

  // console.log('🎯 [handleObject] Обработка объекта:', {
  //   objId,
  //   objType,
  //   layerName,
  //   ctxLayersMapCount: Object.values(ctx.layersMap).flat().length,
  // });

  if (objData.type?.toLowerCase() === 'group') {
    return handleGroup(ctx, objData, layerName);
  }
  if (objData.type === 'marker') {
    return handleMarker(ctx, objData, layerName);
  }
  if (objData.type === 'textEntity') {
    return handleTextEntity(ctx, objData, layerName);
  }

  if (ctx.progressive && objData.type?.toLowerCase() === 'image') {
    return handleProgressiveImage(ctx, objData, layerName);
  }
  return handleGenericObject(ctx, objData, layerName);
}

export const loadObjectsWithLayersRefactored = async (
  canvas: Canvas | StaticCanvas,
  canvasId: string,
  {
    objects,
    backgroundImage,
    textEntityStyles,
  }: {
    version: string;
    objects: any[];
    background?: string;
    backgroundImage?: string | { src?: string; [key: string]: any };
    textEntityStyles?: any;
  },
  isNeedMutateStore = true,
  options?: LoadOptions,
) => {
  // console.log('🎯 [loadObjectsWithLayersRefactored] Starting:');

  // Проверяем что canvas не был disposed - getContext() обращается к this.elements.lower.ctx
  // и кидает TypeError если elements.lower === undefined (после dispose)
  try {
    if (!canvas || !canvas.getContext()) {
      console.warn('Invalid canvas state, aborting loadObjectsWithLayers');
      return;
    }
  } catch {
    console.warn('Canvas was disposed, aborting loadObjectsWithLayers');
    return;
  }

  const progressive = Boolean(options?.progressive) && canvas instanceof Canvas;
  const lockDuringLoad = Boolean(options?.lockDuringLoad) && canvas instanceof Canvas;
  const streamingRender = Boolean(options?.streamingRender) && canvas instanceof Canvas;
  const onProgress = options?.onProgress;
  const logging = Boolean(options?.logging || false);

  if (logging) {
    // optional debug logs
  }

  // Нормализация backgroundImage: может быть строкой URL или объектом из canvas.toJSON()
  // canvas.toJSON() сериализует backgroundImage как объект { type: 'image', src: '...', ... }
  let normalizedBackgroundImage: string | null = null;
  if (backgroundImage) {
    if (typeof backgroundImage === 'string') {
      normalizedBackgroundImage = backgroundImage;
      console.debug(
        '🎨 [loadObjectsWithLayers] backgroundImage is string:',
        normalizedBackgroundImage?.substring(0, 80),
      );
    } else if (typeof backgroundImage === 'object' && backgroundImage.src) {
      // Извлекаем src из сериализованного Fabric Image объекта
      normalizedBackgroundImage = backgroundImage.src;
      console.debug(
        '🎨 [loadObjectsWithLayers] backgroundImage extracted from object:',
        normalizedBackgroundImage?.substring(0, 80),
      );
    } else {
      console.warn('⚠️ [loadObjectsWithLayers] backgroundImage has unexpected format:', {
        type: typeof backgroundImage,
        hasType: !!(backgroundImage as any)?.type,
        hasSrc: !!(backgroundImage as any)?.src,
        keys: Object.keys(backgroundImage || {}).slice(0, 10),
      });
    }
  }

  try {
    // Блокируем канвас перед загрузкой (если требуется)
    if (lockDuringLoad && canvas instanceof Canvas && isNeedMutateStore) {
      const { lockCanvas: lockCanvasInStore } = useCanvasStore.getState();
      lockCanvasInStore(canvasId, 'loading_objects');
      console.debug('🔒 [loadObjectsWithLayers] Канвас заблокирован:', {
        canvasId,
        lockDuringLoad,
        isNeedMutateStore,
      });
    }

    // Очистка канваса и layersMap перед загрузкой (если требуется)
    // Логика определения необходимости очистки вынесена в вызывающий код (SlideCanvas)
    if (options?.clearCanvasBeforeLoad && canvas instanceof Canvas && isNeedMutateStore) {
      const { clearLayer } = useCanvasStore.getState();

      // console.log('🧹 [loadObjectsWithLayers] Очистка перед загрузкой:', {
      //   canvasId,
      //   canvasObjectsBefore: canvas.getObjects().length,
      // });

      // Очищаем все слои в store
      const layers: Layer[] = ['background', 'middle', 'foreground', 'overlay'];
      layers.forEach((layer) => {
        clearLayer(canvasId, layer);
      });

      // Очищаем канвас, сохраняя цвет фона
      const currentBackgroundColor = canvas.backgroundColor;
      canvas.clear();
      if (currentBackgroundColor) {
        canvas.backgroundColor = currentBackgroundColor;
      }
    }

    const { width: slideWidth, height: slideHeight } = extractSlideMetadata({ objects }) || {
      width: 0,
      height: 0,
    };

    const ctx = buildCtx({
      canvas,
      canvasId,
      slideWidth,
      slideHeight,
      progressive,
      lockDuringLoad,
      streamingRender,
      logging,
      onProgress,
      total: objects.length,
      textEntityStyles,
      isNeedMutateStore,
      canvasBackgroundImage: normalizedBackgroundImage,
    });

    // console.log('🔄 [loadObjectsWithLayers] Начинаем обработку объектов:', {
    //   canvasId,
    //   totalObjects: objects.length,
    //   canvasObjectsBefore: canvas instanceof Canvas ? canvas.getObjects().length : 0,
    // });

    for (const objData of objects) {
      try {
        if (
          objData.type?.toLowerCase() === 'image' &&
          objData.imageColor &&
          objData.src?.includes('{color}')
        ) {
          await makeColorizedUrlForObject(objData, objData.imageColor);
        }
        await handleObject(ctx, objData);
      } catch {
        ctx.loaded++;
        ctx.report();
      }
    }

    // console.log('✅ [loadObjectsWithLayers] Обработка объектов завершена:', {
    //   canvasId,
    //   processedObjects: objects.length,
    //   canvasObjectsAfter: canvas instanceof Canvas ? canvas.getObjects().length : 0,
    // });

    if (!ctx.progressive && !ctx.streamingRender) {
      const currentBackgroundColor = (canvas as Canvas).backgroundColor;
      const currentBackgroundImage = (canvas as Canvas).backgroundImage;
      try {
        canvas?.clear();
      } catch {
        // Canvas может быть disposed - clear() вызывает getContext() -> elements.lower.ctx
        console.warn('Canvas was disposed, skipping clear in loadObjectsWithLayers');
        return;
      }
      if (currentBackgroundColor) {
        (canvas as Canvas).backgroundColor = currentBackgroundColor;
      }
      // Восстанавливаем backgroundImage после clear() если он был установлен
      if (currentBackgroundImage) {
        (canvas as Canvas).backgroundImage = currentBackgroundImage;
      }
    }

    await finalizeBackground(ctx);
    await commitLayersToCanvasAndStore(ctx);

    if (!ctx.isBackgroundSetted) {
      (canvas as Canvas).backgroundColor = 'white';
    }

    (canvas as Canvas).requestRenderAll();

    // 🔗 СНАЧАЛА связываем маркеры с контентом (ДО инициализации метабуллитов!)
    linkMarkersWithContentObjects(canvas as Canvas, objects);

    // 🎯 Инициализируем метабуллиты (relinkTextEntitiesWithMarkers вызывается внутри!)
    // ⚠️ ВАЖНО: Не создаем бэкап если прогрессивная загрузка еще идет!
    if (canvas instanceof Canvas) {
      await initializeMetaBulletsFromCanvas(canvas, canvasId, {
        skipBackupIfProgressive: ctx.progressive || false,
      });
    }

    if (ctx.progressive) {
      await postProgressiveFinalize(ctx, objects);

      console.debug('✅ [loadObjectsWithLayers] Progressive finalize completed:', {
        canvasId: ctx.canvasId,
        replacementPromisesCount: ctx.replacementPromises.length,
      });

      // 🎯 КРИТИЧНО: Создаем бэкап ПОСЛЕ завершения прогрессивной загрузки
      // чтобы сохранить реальные изображения, а не плейсхолдеры "Loading..."
      if (canvas instanceof Canvas && canvas._metaBulletSystem) {
        const system = canvas._metaBulletSystem;
        if (system.hasPositionChanges) {
          console.debug(
            '📝 Creating backup after progressive loading completed (position changes detected)',
          );
          const backupManager = (system as any).backupManager;
          if (backupManager) {
            backupManager.requestBackup();
          } else {
            // Fallback к старому методу
            canvas.fire('metaBullet:backup:needed', {
              reason: 'progressive_load_completed',
              hasChanges: true,
              timestamp: Date.now(),
            });
          }
        }
      }
    }

    // 🔓 РАЗБЛОКИРУЕМ КАНВАС ПОСЛЕ ЗАВЕРШЕНИЯ ВСЕХ ОПЕРАЦИЙ (включая progressive)
    // Это гарантирует что все изображения загружены и плейсхолдеры заменены
    if (lockDuringLoad && canvas instanceof Canvas && isNeedMutateStore) {
      try {
        const { unlockCanvas: unlockCanvasInStore, isCanvasLocked: isCanvasLockedInStore } =
          useCanvasStore.getState();

        console.debug('🔓 [loadObjectsWithLayers] Разблокировка канваса...', {
          canvasId,
          isLockedBefore: isCanvasLockedInStore(canvasId),
        });

        unlockCanvasInStore(canvasId);

        // Проверяем что разблокировка прошла успешно
        const isStillLocked = isCanvasLockedInStore(canvasId);
        console.debug('🔓 [loadObjectsWithLayers] Канвас разблокирован после загрузки:', {
          canvasId,
          lockDuringLoad,
          isNeedMutateStore,
          isStillLocked,
          storeState: useCanvasStore.getState().canvasData[canvasId]?.isLocked,
        });

        if (isStillLocked) {
          console.error(
            '❌ [loadObjectsWithLayers] Канвас все еще заблокирован после разблокировки!',
            {
              canvasId,
            },
          );
        }

        (canvas as Canvas)
          .getElement?.()
          ?.dispatchEvent?.(new CustomEvent(CANVAS_EVENTS.PROGRESSIVE_LOAD_COMPLETE));
      } catch (error) {
        console.warn('⚠️ [loadObjectsWithLayers] Ошибка при разблокировке канваса:', error);
      }
    }

    return ctx.layersMap;
  } catch (error) {
    // В случае ошибки разблокируем канвас
    if (lockDuringLoad && canvas instanceof Canvas && isNeedMutateStore) {
      try {
        const { unlockCanvas: unlockCanvasInStore } = useCanvasStore.getState();
        unlockCanvasInStore(canvasId);
        console.debug('🔓 [loadObjectsWithLayers] Канвас разблокирован после ошибки:', {
          canvasId,
          error,
        });
      } catch (unlockError) {
        console.warn('⚠️ [loadObjectsWithLayers] Ошибка при разблокировке канваса:', unlockError);
      }
    }
    throw error;
  } finally {
    // Finally блок оставляем пустым - разблокировка перенесена в основной блок
  }
};

/**
 * Связывает маркеры с контентными объектами по ID
 * @param canvas - Canvas с загруженными объектами
 * @param objects - Массив объектов из конфигурации
 */
function linkMarkersWithContentObjects(canvas: Canvas, objects: any[]): void {
  const allCanvasObjects = canvas.getObjects();

  // Находим все маркеры на канвасе
  const markers = allCanvasObjects.filter(
    (obj): obj is Marker => obj.type === 'marker' && !!obj.isMarker,
  ) as Marker[];

  if (markers.length === 0) {
    console.debug('🔗 No markers found for linking');
    return;
  }

  // Для каждого маркера ищем ВСЕ связанные объекты в конфигурации
  markers.forEach((marker) => {
    // Пропускаем маркеры которые уже связаны с контентом (за исключением placeholder-ов)
    const existingContents = marker.getContentObjects();
    const hasNonPlaceholderContent = existingContents.some((obj) => obj.type !== 'rect');
    if (hasNonPlaceholderContent) {
      return;
    }

    // Ищем ВСЕ объекты в исходной конфигурации, которые должны стать контентом маркера
    // ⚠️ ВАЖНО: Исключаем textEntity - они являются родительскими объектами, а не контентом маркера
    const markerConfigObjects = objects.filter(
      (obj) => obj.type !== 'marker' && obj.type !== 'textEntity' && obj.markerId === marker.id,
    );

    if (markerConfigObjects.length > 0) {
      // Обрабатываем каждый найденный объект контента
      markerConfigObjects.forEach((configObject) => {
        // Ищем соответствующий Fabric объект на канвасе
        const contentFabricObject = allCanvasObjects.find((obj) => obj.id === configObject.id);

        if (contentFabricObject) {
          // Добавляем объект к маркеру
          marker.addContentObject(contentFabricObject);
        } else {
          console.warn('⚠️ Content object not found on canvas:', {
            markerId: marker.id,
            expectedContentObjectId: configObject.id,
            expectedType: configObject.type,
          });
        }
      });
    }
  });
}
